import React from 'react';
import { Box, Container, Typography, Button, Stack, Divider } from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import FacebookIcon from '@mui/icons-material/Facebook';

export function CTA() {
  return (
    <Box sx={{
      py: { xs: 10, md: 14 },
      background: 'linear-gradient(145deg, #0d1f35 0%, #1075BA 60%, #3a9de4 100%)',
      position: 'relative', overflow: 'hidden',
    }}>
      {/* Decorative rings */}
      {[300, 500, 700].map((size, i) => (
        <Box key={i} sx={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: size, height: size, borderRadius: '50%',
          border: '1px solid rgba(255,255,255,0.06)',
          pointerEvents: 'none',
        }} />
      ))}
      <Box sx={{ position: 'absolute', bottom: -80, right: -80, width: 320, height: 320, borderRadius: '50%', background: 'radial-gradient(circle, rgba(248,178,32,0.15) 0%, transparent 70%)', pointerEvents: 'none' }} />

      <Container maxWidth="md" sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <Box sx={{ fontSize: '3rem', mb: 2 }}>🚀</Box>
        <Typography variant="h2" sx={{ fontSize: { xs: '2.2rem', md: '3.2rem' }, color: '#fff', mb: 2.5, fontWeight: 800 }}>
          Ready to Take Control of{' '}
          <Box component="span" sx={{ color: '#F8B220' }}>Your Fleet?</Box>
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '1.1rem', mb: 5, maxWidth: 520, mx: 'auto', lineHeight: 1.75 }}>
          Start your journey with AutoChecker. See how it works for your fleet in a personalized demo.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} justifyContent="center">
          <Button
            variant="contained"
            size="large"
            sx={{
              bgcolor: '#F8B220', color: '#0d1f35',
              '&:hover': { bgcolor: '#ffc94d', boxShadow: '0 12px 40px rgba(248,178,32,0.45)', transform: 'translateY(-2px)' },
              fontSize: '1.05rem', py: 1.75, px: 5,
              fontWeight: 700, transition: 'all 0.25s ease',
              boxShadow: '0 8px 30px rgba(248,178,32,0.35)',
            }}
          >
            Book a Demo
          </Button>
          <Button
            variant="outlined"
            size="large"
            sx={{
              borderColor: 'rgba(255,255,255,0.4)', color: '#fff',
              '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.08)' },
              fontSize: '1.05rem', py: 1.75, px: 4,
            }}
          >
            Schedule a Demo
          </Button>
        </Stack>
        <Typography sx={{ color: 'rgba(255,255,255,0.45)', mt: 3, fontSize: '0.85rem' }}>
          ✓ No commitment &nbsp;·&nbsp; ✓ Personalized walkthrough &nbsp;·&nbsp; ✓ Cancel anytime
        </Typography>
      </Container>
    </Box>
  );
}

const footerLinks = {
  Product: ['Features', 'Pricing', 'Changelog', 'Roadmap'],
  Company: ['About Us', 'Blog', 'Careers', 'Press'],
  Support: ['Help Center', 'Documentation', 'API Docs', 'Status'],
  Legal: ['Privacy Policy', 'Terms of Service', 'Cookie Policy'],
};

export function Footer() {
  return (
    <Box sx={{ bgcolor: '#0d1f35', pt: 8, pb: 4 }}>
      <Container maxWidth="lg">
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr 1fr 1fr 1fr' }, gap: 5, mb: 6 }}>
          {/* Brand */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              {/* <Box sx={{ width: 36, height: 36, borderRadius: '10px', background: 'linear-gradient(135deg, #1075BA, #3a9de4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <DirectionsCarIcon sx={{ color: '#fff', fontSize: 18 }} />
              </Box> */}
              <Typography sx={{ fontWeight: 800, color: '#fff', fontSize: '1.15rem' }}>
                Auto<Box component="span" sx={{ color: '#3a9de4' }}>Checker</Box>
              </Typography>
            </Box>
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.875rem', lineHeight: 1.75, mb: 3, maxWidth: 200 }}>
              Real-time fleet monitoring and driver management platform.
            </Typography>
            <Stack direction="row" gap={1}>
              {[TwitterIcon, LinkedInIcon, FacebookIcon].map((Icon, i) => (
                <Box key={i} sx={{ width: 36, height: 36, borderRadius: '8px', bgcolor: 'rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', '&:hover': { bgcolor: 'rgba(16,117,186,0.4)' }, transition: 'all 0.2s' }}>
                  <Icon sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 18 }} />
                </Box>
              ))}
            </Stack>
          </Box>

          {Object.entries(footerLinks).map(([section, links]) => (
            <Box key={section}>
              <Typography sx={{ fontWeight: 700, color: '#fff', fontSize: '0.85rem', mb: 2.5, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{section}</Typography>
              <Stack gap={1.5}>
                {links.map((link) => (
                  <Typography key={link} sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.875rem', cursor: 'pointer', '&:hover': { color: '#3a9de4' }, transition: 'color 0.2s' }}>
                    {link}
                  </Typography>
                ))}
              </Stack>
            </Box>
          ))}
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mb: 3 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.8rem' }}>
            © {new Date().getFullYear()} AutoChecker. All rights reserved.
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#4ade80', animation: 'pulse 2s ease-in-out infinite' }} />
            <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.8rem' }}>All systems operational</Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
